import { Link } from 'react-router-dom';
import { useScrollAnimation } from '../../../hooks/useScrollAnimation';

export default function About() {
  const { ref: contentRef, isVisible: contentVisible } = useScrollAnimation();
  const { ref: imageRef, isVisible: imageVisible } = useScrollAnimation();

  return (
    <section id="sobre" className="py-16 sm:py-20 lg:py-28 bg-gradient-to-br from-petrol-900 to-petrol-700" aria-labelledby="about-heading">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-16 items-center">

          {/* Content */}
          <div
            ref={contentRef as React.RefObject<HTMLDivElement>}
            className={`transition-all duration-700 ease-out ${contentVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-10'}`}
          >
            <div className="inline-flex items-center gap-2 bg-strategic-green-500/20 backdrop-blur-sm border border-strategic-green-400/30 px-4 py-2 rounded-full mb-4 sm:mb-6" aria-hidden="true">
              <i className="ri-building-line text-strategic-green-400 text-base sm:text-lg"></i>
              <span className="text-strategic-green-300 font-bold text-xs sm:text-sm uppercase tracking-wide">Quem Somos</span>
            </div>

            <h2 id="about-heading" className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4 sm:mb-6" style={{ fontFamily: "'Playfair Display', serif" }}>
              Especialistas em{' '}
              <span className="text-strategic-green-400">Gestão de Riscos</span>{' '}
              e Seguros
            </h2>

            <div className="space-y-4 sm:space-y-6 text-sm sm:text-base lg:text-lg text-gray-100 leading-relaxed mb-6 sm:mb-8">
              <p>
                A <strong className="text-white">IBX Brasil</strong> é referência nacional em gestão profissional de riscos e contratação estratégica de seguros, com mais de 25 anos de experiência no mercado.
              </p>
              <p>
                Nossa missão é <strong className="text-white">profissionalizar a contratação de seguros</strong>, eliminando custos desnecessários e garantindo proteção adequada ao patrimônio de empresas e pessoas físicas.
              </p>
              <p>
                Aplicamos metodologia técnica de análise de riscos, negociação estratégica com seguradoras e gestão financeira de contratos para entregar <strong className="text-strategic-green-400">economia real</strong> e <strong className="text-strategic-green-400">segurança garantida</strong>.
              </p>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 sm:gap-6 mb-6 sm:mb-8">
              {[
                { value: '25+', label: 'Anos de Mercado' },
                { value: '500+', label: 'Clientes' },
                { value: '35%', label: 'Economia Média' }
              ].map((stat, i) => (
                <div key={i} className="text-center">
                  <div
                    className="text-3xl sm:text-4xl lg:text-5xl font-bold text-strategic-green-400 mb-1 sm:mb-2"
                    style={{ fontFamily: "'Playfair Display', serif" }}
                    aria-label={`${stat.value} ${stat.label}`}
                  >
                    {stat.value}
                  </div>
                  <p className="text-xs sm:text-sm text-gray-200 font-medium">{stat.label}</p>
                </div>
              ))}
            </div>

            <Link
              to="/quem-somos"
              className="inline-flex items-center justify-center gap-2 sm:gap-3 bg-action-orange-500 text-white px-6 sm:px-8 py-4 sm:py-5 rounded-xl text-base sm:text-lg font-bold hover:bg-action-orange-600 transition-all duration-300 shadow-xl hover:shadow-2xl hover:scale-105 whitespace-nowrap cursor-pointer w-full sm:w-auto"
              aria-label="Conhecer mais sobre a IBX Brasil"
            >
              <i className="ri-arrow-right-line text-xl" aria-hidden="true"></i>
              Conhecer a IBX Brasil
            </Link>
          </div>

          {/* Image */}
          <div
            ref={imageRef as React.RefObject<HTMLDivElement>}
            className={`relative mt-8 lg:mt-0 transition-all duration-700 ease-out delay-200 ${imageVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-10'}`}
          >
            <div className="relative rounded-2xl overflow-hidden shadow-2xl bg-petrol-100" style={{ aspectRatio: '4/5' }}>
              <img
                src="imagens/secoes/about-1.jpg"
                alt="Equipe IBX Brasil reunida em reunião estratégica de gestão de riscos"
                className="w-full h-full object-cover object-top"
                loading="lazy"
                decoding="async"
                width="800"
                height="1000"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-petrol-900/40 to-transparent" aria-hidden="true"></div>
            </div>

            <div className="absolute -bottom-4 sm:-bottom-6 left-4 sm:left-6 right-4 sm:right-6 bg-white rounded-xl sm:rounded-2xl p-4 sm:p-6 shadow-2xl">
              <div className="flex items-center justify-between gap-4">
                <div className="flex items-center gap-3 sm:gap-4">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-strategic-green-500 to-strategic-green-600 rounded-lg flex items-center justify-center flex-shrink-0" aria-hidden="true">
                    <i className="ri-shield-check-fill text-xl sm:text-2xl text-white"></i>
                  </div>
                  <div>
                    <div
                      className="text-xl sm:text-2xl lg:text-3xl font-bold text-petrol-900"
                      style={{ fontFamily: "'Playfair Display', serif" }}
                      aria-label="Mais de R$ 50 milhões em patrimônio protegido"
                    >
                      R$ 50M+
                    </div>
                    <p className="text-xs sm:text-sm text-petrol-700 font-medium">Patrimônio Protegido</p>
                  </div>
                </div>
                <i className="ri-arrow-right-up-line text-2xl sm:text-3xl text-strategic-green-600" aria-hidden="true"></i>
              </div>
            </div>
          </div>

        </div>
      </div>
    </section>
  );
}
